import {
  type IntegrationsApiClient,
  type SyncResponse,
  type Paths,
  getCurrentSubdomain,
} from '@webapp/core/services/IntegrationsAPIClient/IntegrationsAPIClient.service'
import {
  type IntegrationConstructor,
  type Integration,
} from '@webapp/platform/integrations/entities'
import { type EntitySyncConfigurationConstructor } from '@webapp/platform/integrations/entities/EntitySyncConfiguration/entitySyncConfiguration'
import { type EntitySyncConfigurationEntityEnum } from '@webapp/platform/integrations/entities/enums/EntitySyncConfigurationEntityEnum'
import { mapEntitySyncConfigurationApiModelToConstructor } from '../../mappers/mapEntitySyncConfigurationApiModelToConstructor'
import { mapIntegrationApiModel } from '../../mappers/mapIntegrationApiModel'

interface IDependency {
  httpClient: typeof IntegrationsApiClient
}

export interface IIntegrationRepository {
  listIntegrations(): Promise<IntegrationConstructor[]>
  deleteIntegration(
    integrationId: Integration['id'],
    hardDelete?: boolean
  ): Promise<void>
  updateEntitySyncConfiguration(
    integrationId: Integration['id'],
    entityName: EntitySyncConfigurationEntityEnum,
    isEnabled: boolean
  ): Promise<EntitySyncConfigurationConstructor>
  pullMasterData(
    integrationId: Integration['id'],
    entityName: EntitySyncConfigurationEntityEnum,
    forceResync?: boolean
  ): Promise<void>
  getEntitySyncRecords(params: {
    integrationId: string
    localEntity: Paths.ListSync.Parameters.LocalEntity
    localEntityRef: string | number
  }): Promise<SyncResponse[] | undefined>
}

export class IntegrationRepository implements IIntegrationRepository {
  constructor(private readonly deps: IDependency) {}

  async listIntegrations(): Promise<IntegrationConstructor[]> {
    try {
      const response = await this.deps.httpClient.listIntegrations({
        tenant: getCurrentSubdomain(),
      })
      return response.data.map((integrationApiModel) =>
        mapIntegrationApiModel(integrationApiModel)
      )
    } catch (error: unknown) {
      throw error
    }
  }

  async deleteIntegration(
    integrationId: Integration['id'],
    hardDelete: boolean = false
  ): Promise<void> {
    try {
      await this.deps.httpClient.deleteIntegration({
        integration_id: integrationId,
        hard_delete: hardDelete,
      })
    } catch (error: any) {
      throw error
    }
  }

  async updateEntitySyncConfiguration(
    integrationId: Integration['id'],
    entityName: EntitySyncConfigurationEntityEnum,
    isEnabled: boolean
  ): Promise<EntitySyncConfigurationConstructor> {
    try {
      const response =
        await this.deps.httpClient.createOrUpdateEntitySyncConfiguration(
          {
            integration_id: integrationId,
            entity_name: entityName,
          },
          {
            is_enabled: isEnabled,
          }
        )

      return mapEntitySyncConfigurationApiModelToConstructor(response.data)
    } catch (e: unknown) {
      throw e
    }
  }

  async pullMasterData(
    integrationId: Integration['id'],
    entityName: EntitySyncConfigurationEntityEnum,
    forceResync?: boolean
  ): Promise<void> {
    const params = {
      entity: entityName,
      ...(forceResync ? { force_resync: true } : {}),
    }

    try {
      await this.deps.httpClient.post(
        `/integrations/${integrationId}/pull-master-data/`,
        params
      )
    } catch (e: unknown) {
      throw e
    }
  }

  async getEntitySyncRecords(params: {
    integrationId: string
    localEntity: Paths.ListSync.Parameters.LocalEntity
    localEntityRef: string | number
  }): Promise<SyncResponse[] | undefined> {
    try {
      const response = await this.deps.httpClient.listSync({
        integration_id: params.integrationId,
        local_entity: params.localEntity,
        local_entity_ref: params.localEntityRef.toString(),
        'X-Procurify-Tenant': getCurrentSubdomain(),
      })
      return response.data
    } catch (e: any) {
      // Throw non-404 errors so they can be caught by error handlers
      if (e?.status !== 404) {
        throw e
      }
      return undefined
    }
  }
}
